import axios from "../axios";

/**
 * Store product list API — for the store/catalog product list only.
 * Do not use the main app's product/service getProducts here.
 * GET /api/store/products — index; GET /api/store/products/:code — show.
 */
const STORE_API_BASE = "/store";

const CONFIG_KEYS = [
  "height",
  "width",
  "depth",
  "external_color",
  "internal_color",
  "carcass_material",
  "shutter_material",
];

const buildConfigParams = (config) => {
  if (!config || typeof config !== "object") return {};
  const params = {};
  CONFIG_KEYS.forEach((key) => {
    const v = config[key];
    if (v === undefined || v === null) return;
    if (typeof v === "string" && v.trim() === "") return;
    if ((key === "height" || key === "width" || key === "depth") && v !== "") {
      const n = Number(v);
      if (!Number.isNaN(n)) params[key] = n;
    } else {
      params[key] = String(v).trim();
    }
  });
  return params;
};

/**
 * GET store products (list for store catalog).
 *
 * This is used for the public Zerobuild /store catalog.
 *
 * @param {{ searchTerm?: string, page?: number, limit?: number }} options
 * @returns {Promise<{ list: Array, search: { query: string, is_search: boolean } }>}
 */
export const getProducts = async (options = {}) => {
  const { searchTerm, page = 1, limit = 100 } = options;
  const params = {
    page,
    limit,
    ...(searchTerm && searchTerm.trim() && { q: searchTerm.trim() }),
  };
  const response = await axios.get(`${STORE_API_BASE}/products`, { params });
  const body = response?.data ?? response;

  let list = [];
  if (body?.data?.products) list = body.data.products;
  else if (body?.products) list = body.products;
  else if (Array.isArray(body?.data)) list = body.data;
  else if (Array.isArray(body)) list = body;
  else if (
    body?.data &&
    typeof body.data === "object" &&
    !Array.isArray(body.data)
  )
    list = Object.values(body.data).flat().filter(Boolean);

  const query = searchTerm ?? "";
  return {
    list: Array.isArray(list) ? list : [],
    search: { query, is_search: !!query.trim() },
  };
};

/**
 * POST /api/store/cart_project
 * Creates/reuses project + folder hierarchy and adds product as a project item.
 */
export const addStoreProductToCartProject = async (
  storeProductId,
  { folderPath = [], projectName } = {}
) => {
  if (!storeProductId) {
    throw new Error("store_product_id is required");
  }
  const body = {
    store_product_id: storeProductId,
  };
  if (Array.isArray(folderPath) && folderPath.length > 0) {
    body.folder_path = folderPath;
  }
  if (projectName && String(projectName).trim()) {
    body.project_name = String(projectName).trim();
  }
  const response = await axios.post(`${STORE_API_BASE}/cart_project`, body);
  try {
    // Refresh/store latest session immediately after guest cart_project flow.
    await axios.get(`${STORE_API_BASE}/session`);
  } catch (e) {
    // Non-blocking: cart_project already succeeded.
  }
  return response;
};

// GET /api/store/products/:code — show; optional config and feature_config (feature name -> value for variants without product_id).
const getCatalogProduct = async (
  productId,
  config = null,
  featureConfig = null
) => {
  const encodedProductId = encodeURIComponent(String(productId ?? "").trim());
  const configParams = config ? buildConfigParams(config) : {};
  const params = {
    ...(Object.keys(configParams).length > 0 ? { config: configParams } : {}),
  };
  if (
    featureConfig &&
    typeof featureConfig === "object" &&
    Object.keys(featureConfig).length > 0
  ) {
    params.feature_config = featureConfig;
  }
  const response = await axios.get(
    `${STORE_API_BASE}/products/${encodedProductId}`,
    {
      params: Object.keys(params).length ? params : undefined,
    }
  );
  return response;
};

/**
 * POST /api/store/products/create — find or create a product for feature_values (base product_id + overlay).
 * Body: { product_id: number, feature_values: { [featureName]: string } }
 * 200 { success: true, data: { id: number } }; 404 base not found; 422 feature_values missing or invalid.
 */
export const createProduct = async (productId, featureValues) => {
  if (!productId || !featureValues || typeof featureValues !== "object") {
    throw new Error("product_id and feature_values are required");
  }
  const body = {
    product_id:
      typeof productId === "number"
        ? productId
        : parseInt(String(productId), 10),
    feature_values: featureValues,
  };
  const response = await axios.post(`${STORE_API_BASE}/products/create`, body, {
    headers: { "Content-Type": "application/json" },
  });
  return response;
};

export const getBrochureForPrint = async () => {
  return await axios.get(`${STORE_API_BASE}/brochure_print`);
};

export const getCatalogBrochureForPrint = async (params = {}) => {
  return await axios.get(`${STORE_API_BASE}/catalog_brochure_print`, {
    params,
  });
};

/**
 * Zerobuild Store API helpers for projects, registration and image tagging.
 * These helpers encapsulate the HTTP calls and document the expected payloads.
 */

/**
 * Register a new Zerobuild store user.
 *
 * Backend endpoint: POST /store/register
 *
 * @param {{ name: string, mobile: string, companyName: string, profileType: string }} params
 * @returns {Promise<any>} Response body from the API.
 */
export const registerStoreUser = async ({
  name,
  mobile,
  companyName,
  profileType,
}) => {
  const body = {
    name,
    mobile,
    company_name: companyName,
    profile_type: profileType,
  };
  const response = await axios.post(`${STORE_API_BASE}/register`, body);
  return response;
};

/**
 * Verify OTP for a Zerobuild store user.
 *
 * Backend endpoint: POST /store/verify_otp
 *
 * @param {{ mobile: string, otp: string }} params
 * @returns {Promise<any>} Response body from the API.
 */
export const verifyStoreOtp = async ({ mobile, otp }) => {
  const response = await axios.post(`${STORE_API_BASE}/verify_otp`, {
    mobile,
    otp,
  });
  return response;
};

/**
 * Fetch all Zerobuild store projects for the current user.
 *
 * Backend endpoint (ZB namespace): GET /store/projects
 *
 * NOTE:
 * - This uses the Zb::UsersController/Store::Project stack as per backend spec.
 * - REACT_APP_API_URL already includes the /api prefix, so this becomes
 *   /api/store/projects on the wire.
 *
 * @returns {Promise<any[]>} Array of project objects.
 */
export const getStoreProjects = async () => {
  const response = await axios.get(`/store/projects`);
  return response;
};

/** Default name for the first Zerobuild quote project (`POST /store/cart_project`). */
export const SAMPLE_QUOTE_PROJECT_NAME = "Sample Quote";

/** Buyer-facing default for the first quote when no name is collected. */
export const MY_FIRST_QUOTE_PROJECT_NAME = "My First Quote";

const DEFAULT_STORE_PROJECT_NAME_SET = new Set(
  [MY_FIRST_QUOTE_PROJECT_NAME, SAMPLE_QUOTE_PROJECT_NAME, "Sample Order"].map(
    (name) => name.toLowerCase()
  )
);

export function isStoreProjectDraft(project) {
  const status = String(project?.status ?? "")
    .trim()
    .toLowerCase();
  return status === "" || status === "draft";
}

export function isDefaultStoreProjectName(name) {
  const trimmed = String(name ?? "").trim();
  if (!trimmed) return true;
  return DEFAULT_STORE_PROJECT_NAME_SET.has(trimmed.toLowerCase());
}

export function buyerHasExistingStoreProjects(projects, hasProjectsFlag) {
  if (hasProjectsFlag === true) return true;
  return Array.isArray(projects) && projects.length > 0;
}

export function countStorePlacedOrders(projects, { excludeProjectId } = {}) {
  const excludeId = excludeProjectId == null ? null : Number(excludeProjectId);
  return (Array.isArray(projects) ? projects : []).filter((project) => {
    const id = Number(project?.id ?? project?.project_id);
    if (
      excludeId != null &&
      Number.isFinite(excludeId) &&
      Number.isFinite(id) &&
      id === excludeId
    ) {
      return false;
    }
    return !isStoreProjectDraft(project);
  }).length;
}

/** True when creating a 2nd+ quote/order (any prior project exists). */
export function shouldPromptStoreProjectName({ projects, hasProjects }) {
  return buyerHasExistingStoreProjects(projects, hasProjects);
}

/** True when placing a 2nd+ order while the quote still has a default name. */
export function shouldPromptStoreProjectNameOnPlace({
  projects,
  projectId,
  projectName,
}) {
  return (
    countStorePlacedOrders(projects, { excludeProjectId: projectId }) >= 1 &&
    isDefaultStoreProjectName(projectName)
  );
}

export function coerceStoreProjectsArray(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  // GET /store/projects → { success, data: [projects] }
  if (Array.isArray(payload?.data)) return payload.data;
  // axios → { data: { success, data: [projects] } }
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.projects)) return payload.projects;
  return [];
}

export function sortStoreProjectsEarliestFirst(projects) {
  const arr = Array.isArray(projects) ? [...projects] : [];
  arr.sort((a, b) => {
    const ta =
      Date.parse(String(a?.created_at ?? a?.createdAt ?? "").trim()) || 0;
    const tb =
      Date.parse(String(b?.created_at ?? b?.createdAt ?? "").trim()) || 0;
    if (ta !== tb) return ta - tb;
    const ida = Number(a?.id ?? a?.project_id);
    const idb = Number(b?.id ?? b?.project_id);
    const na = Number.isNaN(ida) ? 0 : ida;
    const nb = Number.isNaN(idb) ? 0 : idb;
    return na - nb;
  });
  return arr;
}

export function sortedStoreProjectsFromApiPayload(payload) {
  return sortStoreProjectsEarliestFirst(coerceStoreProjectsArray(payload));
}

/** Numeric store user id from session (`currentUser`). */
export function normalizeStoreUserId(user) {
  const raw = user?.id ?? user?.user_id;
  if (raw == null || raw === "") return null;
  if (typeof raw === "string" && raw.startsWith("admin_")) return null;
  const n = Number(raw);
  return Number.isNaN(n) || n <= 0 ? null : n;
}

export function isOwnStoreProject(project, currentUserId) {
  if (currentUserId == null) return true;
  const ownerId = Number(project?.user_id);
  if (Number.isNaN(ownerId) || ownerId <= 0) return true;
  return ownerId === currentUserId;
}

/** Split buyer-account projects: teammates first, signed-in user last. */
export function partitionStoreProjectsByOwner(projects, currentUserId) {
  const others = [];
  const own = [];
  for (const project of Array.isArray(projects) ? projects : []) {
    if (isOwnStoreProject(project, currentUserId)) own.push(project);
    else others.push(project);
  }
  return { others, own };
}

export function sortStoreProjectsForBuyerAccount(projects, currentUserId) {
  const { others, own } = partitionStoreProjectsByOwner(
    projects,
    currentUserId
  );
  return [
    ...sortStoreProjectsEarliestFirst(others),
    ...sortStoreProjectsEarliestFirst(own),
  ];
}

export function sortedStoreProjectsForBuyerAccount(payload, currentUser) {
  const list = sortStoreProjectsEarliestFirst(
    coerceStoreProjectsArray(payload)
  );
  const currentUserId = normalizeStoreUserId(currentUser);
  if (currentUserId == null) return list;
  return sortStoreProjectsForBuyerAccount(list, currentUserId);
}

/**
 * Quote label for pickers / lists. Other users on the account get `(user_name)` suffix.
 * @param {object} project
 * @param {number | null} currentUserId
 * @param {{ includeOrderIdentifier?: boolean }} [opts]
 */
export function storeQuoteDisplayLabel(
  project,
  currentUserId,
  { includeOrderIdentifier = false } = {}
) {
  const pid = project?.id ?? project?.project_id;
  const base = String(project?.name || `Quote ${pid}`).trim();
  const orderId = String(project?.order_identifier ?? "").trim();
  let label = includeOrderIdentifier && orderId ? `${base} (${orderId})` : base;
  if (!isOwnStoreProject(project, currentUserId)) {
    const owner = String(project?.user_name ?? "").trim() || "Unknown";
    label = `${label} (${owner})`;
  }
  return label;
}

/** Matches `serialize_project` / sidebar `projects[]` (`can_manage_project_items`). */
export function projectCanManageItems(project) {
  return project?.can_manage_project_items === true;
}

/** Session flag: user clicked sample cabinet CTA before login; fulfill after auth modal closes. */
export const PENDING_SAMPLE_CABINET_QUOTE_KEY =
  "zb_pending_sample_cabinet_quote";

export function hasPendingSampleCabinetIntent() {
  try {
    return sessionStorage.getItem(PENDING_SAMPLE_CABINET_QUOTE_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearPendingSampleCabinetIntent() {
  try {
    sessionStorage.removeItem(PENDING_SAMPLE_CABINET_QUOTE_KEY);
  } catch {
    // ignore
  }
}

/** Sales line for panel sample / quote assistance promo banners. */
export const STORE_SALES_PHONE_MSISDN = "919150469996";
export const STORE_SALES_PHONE_TEL = `+${STORE_SALES_PHONE_MSISDN}`;

const QUOTE_DRAWING_WHATSAPP_MESSAGE = "Need quote for my project";

export function storeQuoteDrawingWhatsAppUrl() {
  return `https://wa.me/${STORE_SALES_PHONE_MSISDN}?text=${encodeURIComponent(
    QUOTE_DRAWING_WHATSAPP_MESSAGE
  )}`;
}

export const PENDING_PANEL_SAMPLE_PHONE_KEY = "zb_pending_panel_sample_phone";

export function setPendingPanelSamplePhoneIntent() {
  try {
    sessionStorage.setItem(PENDING_PANEL_SAMPLE_PHONE_KEY, "1");
  } catch {
    // ignore
  }
}

export function hasPendingPanelSamplePhoneIntent() {
  try {
    return sessionStorage.getItem(PENDING_PANEL_SAMPLE_PHONE_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearPendingPanelSamplePhoneIntent() {
  try {
    sessionStorage.removeItem(PENDING_PANEL_SAMPLE_PHONE_KEY);
  } catch {
    // ignore
  }
}

export const PENDING_QUOTE_DRAWING_WHATSAPP_KEY =
  "zb_pending_quote_drawing_whatsapp";

export function setPendingQuoteDrawingWhatsAppIntent() {
  try {
    sessionStorage.setItem(PENDING_QUOTE_DRAWING_WHATSAPP_KEY, "1");
  } catch {
    // ignore
  }
}

export function hasPendingQuoteDrawingWhatsAppIntent() {
  try {
    return sessionStorage.getItem(PENDING_QUOTE_DRAWING_WHATSAPP_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearPendingQuoteDrawingWhatsAppIntent() {
  try {
    sessionStorage.removeItem(PENDING_QUOTE_DRAWING_WHATSAPP_KEY);
  } catch {
    // ignore
  }
}

export async function sendStoreSlackNotification(template) {
  try {
    await axios.post(`${STORE_API_BASE}/slack_notifications`, {
      template,
    });
  } catch {}
}

/** Navigate to sales phone; Slack notification runs in parallel (not awaited). */
export function executePanelSamplePhoneIntent() {
  window.location.href = `tel:${STORE_SALES_PHONE_TEL}`;

  void sendStoreSlackNotification("catalog_talk_to_sales");
}

/** Open WhatsApp for drawing share; Slack notification runs in parallel (not awaited). */
export function executeQuoteDrawingWhatsAppIntent() {
  window.open(storeQuoteDrawingWhatsAppUrl(), "_blank", "noopener,noreferrer");

  void sendStoreSlackNotification("catalog_share_drawing");
}

/** Frozen sample cabinet quotes must not appear in "Add to Quote" pickers. */
export function isStoreSampleQuote(project) {
  return project?.sample === true;
}

/** Draft quotes that accept new catalog products (excludes sample cabinet quotes). */
export function isStoreDraftQuoteSelectable(proj) {
  if (proj?.frozen === true) return false;
  if (isStoreSampleQuote(proj)) return false;
  const status = String(proj?.status ?? "")
    .trim()
    .toLowerCase();
  return status === "" || status === "draft";
}

export function filterStoreDraftQuotesSelectable(projects) {
  return (Array.isArray(projects) ? projects : []).filter(
    isStoreDraftQuoteSelectable
  );
}

/** Draft quotes the signed-in user may add catalog products to (excludes teammates). */
export function filterOwnStoreDraftQuotesForAdd(projects, currentUser) {
  const currentUserId = normalizeStoreUserId(currentUser);
  return filterStoreDraftQuotesSelectable(projects).filter((project) =>
    isOwnStoreProject(project, currentUserId)
  );
}

/** Whether the row lacks `can_manage_project_items` from Projects API enrichment. */
export function projectRowNeedsManageFlagFromProjectsApi(row) {
  return !(row != null && typeof row.can_manage_project_items === "boolean");
}

/**
 * Overlay `GET /store/projects` rows onto catalog rows by id (`id` / `project_id`).
 * @param {object[]} catalogRows
 * @param {object[]} apiRows
 */
export function mergeStoreProjectRowsById(catalogRows, apiRows) {
  if (!Array.isArray(catalogRows) || catalogRows.length === 0)
    return catalogRows ?? [];
  if (!Array.isArray(apiRows) || apiRows.length === 0) return catalogRows;

  const enrichById = new Map();
  for (const row of apiRows) {
    const raw = row?.id ?? row?.project_id;
    const n = Number(raw);
    if (!Number.isNaN(n) && n > 0) enrichById.set(n, row);
  }
  return catalogRows.map((row) => {
    const raw = row?.id ?? row?.project_id;
    const n = Number(raw);
    if (Number.isNaN(n) || n <= 0) return row;
    const meta = enrichById.get(n);
    return meta ? { ...row, ...meta } : row;
  });
}

/** Union catalog rows with GET /store/projects (adds teammate quotes on the same buyer). */
export function mergeBuyerAccountProjectLists(catalogRows, apiRows) {
  const byId = new Map();
  for (const row of catalogRows ?? []) {
    const n = Number(row?.id ?? row?.project_id);
    if (!Number.isNaN(n) && n > 0) byId.set(n, row);
  }
  for (const row of apiRows ?? []) {
    const n = Number(row?.id ?? row?.project_id);
    if (!Number.isNaN(n) && n > 0) {
      byId.set(n, byId.has(n) ? { ...byId.get(n), ...row } : row);
    }
  }
  return Array.from(byId.values());
}

/** First draft-order project the current user may mutate; `null` if none. */
export function firstManageableProjectIdFromList(projects) {
  const sorted = sortStoreProjectsEarliestFirst(projects);
  const first = sorted.find(
    (p) => projectCanManageItems(p) && !isStoreSampleQuote(p)
  );
  if (!first) return null;
  const raw = first?.id ?? first?.project_id;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

/**
 * Create a new Zerobuild store project.
 *
 * Backend endpoint (ZB namespace): POST /store/projects
 * Body: nested params so Rails strong params see :name (not only a top-level key).
 * We send both `project` and `store_project` — use whichever your controller
 * `params.require(...).permit(:name)` expects; the other key is ignored.
 *
 * @param {{ name: string }} params
 * @returns {Promise<any>} Newly created project.
 */
/**
 * POST /api/store/sample_cabinet_quote — frozen draft quote with the sample cabinet SKU.
 * @returns {Promise<any>}
 */
export const createSampleCabinetQuote = async () => {
  const response = await axios.post(`${STORE_API_BASE}/sample_cabinet_quote`);
  return response;
};

export const createStoreProject = async ({ name }) => {
  const trimmed =
    typeof name === "string" ? name.trim() : String(name ?? "").trim();
  if (!trimmed) {
    throw new Error("Project name is required");
  }
  const nested = { name: trimmed };
  const response = await axios.post(`/store/projects`, {
    project: nested,
    store_project: nested,
  });
  return response;
};

/**
 * Zerobuild Store Project Items API (zb/v1 namespace)
 *
 * Read endpoint uses: /api/store/projects/:project_id
 * Mutations use: /api/store/projects/:project_id/project_items
 */

/**
 * List all items in a project.
 *
 * Backend endpoint: GET /api/store/projects/:project_id
 * Returns all items for the project (ordered by position)
 *
 * @param {number|string} projectId
 * @returns {Promise<{ success?: boolean, data: object[] }>} Nested tree in `data` (each node has `children`).
 */
export const getStoreProjectItems = async (projectId) => {
  /** Interceptor already returns `response.data` from axios — do not read `.data` again. */
  return axios.get(`/store/projects/${projectId}`);
};

/**
 * List folder children for quote folder picker (no products).
 *
 * Backend endpoint: GET /api/store/projects/:project_id/folders
 *
 * @param {number|string} projectId
 * @param {{ parentId?: number|string|null }} [options]
 * @returns {Promise<{ success?: boolean, data: { parent_id: number|null, folders: object[], recent_folders?: { id: number, path: string[], path_label: string }[] } }>}
 */
export const getStoreProjectFolders = async (projectId, { parentId } = {}) => {
  const params = {};

  if (parentId != null && parentId !== "") params.parent_id = parentId;

  return axios.get(`/store/projects/${projectId}/folders`, { params });
};

export const getStoreProjectInstallation = async (projectId) => {
  return axios.get(`/store/projects/${projectId}/installation`);
};

/**
 * Fetch hardware list for a project.
 *
 * Backend endpoint: GET /api/store/projects/:project_id/hardware_list
 *
 * @param {number|string} projectId
 * @returns {Promise<any>} Hardware list payload from API.
 */
export const getStoreProjectHardwareList = async (projectId) => {
  return axios.get(`/store/projects/${projectId}/hardware_list`);
};

export const getStoreProjectSummary = async (projectId) => {
  return axios.get(`/store/projects/${projectId}/summary`);
};

export const getStoreProjectEstimate = async (projectId) => {
  return axios.get(`/store/projects/${projectId}/estimate`);
};

export const getStoreProjectProformaInvoice = async (projectId) => {
  return axios.get(`/store/projects/${projectId}/proforma_invoice`);
};

export const resetStoreProjectProformaInvoice = async (projectId) => {
  return axios.post(`/store/projects/${projectId}/reset_proforma_invoice`);
};

export const updateStoreProject = async (projectId, params = {}) => {
  return axios.patch(`/store/projects/${projectId}`, params);
};

/**
 * POST /api/store/projects/:project_id/duplicate
 * @param {number|string} projectId
 * @returns {Promise<any>}
 */
export const duplicateStoreProject = async (projectId) => {
  return axios.post(`/store/projects/${projectId}/duplicate`);
};

/**
 * POST /api/store/projects/:project_id/copy_to_user
 * @param {number|string} projectId
 * @param {number|string} targetUserId
 * @returns {Promise<any>}
 */
export const copyStoreProjectToUser = async (projectId, targetUserId) => {
  return axios.post(`/store/projects/${projectId}/copy_to_user`, {
    target_user_id: targetUserId,
  });
};

export const updateStoreProjectStatus = async (
  projectId,
  status,
  mode = null,
  rejectionRemarks = null
) => {
  const payload = { status };
  if (mode) payload.mode = mode;
  if (rejectionRemarks) payload.rejection_remarks = rejectionRemarks;
  return axios.patch(`/store/projects/${projectId}/update_status`, payload);
};

export const refreshStoreProjectQuotePrices = async (projectId) => {
  return axios.post(`/store/projects/${projectId}/refresh_quote_prices`);
};

/**
 * Show one item.
 *
 * Backend endpoint: GET /api/store/projects/:project_id/project_items/:id
 *
 * @param {number|string} projectId
 * @param {number|string} itemId
 * @returns {Promise<any>} Item details.
 */
export const getStoreProjectItem = async (projectId, itemId) => {
  const response = await axios.get(
    `/store/projects/${projectId}/project_items/${itemId}`
  );
  return response;
};

/**
 * Create a new project item (folder or product).
 *
 * Backend endpoint: POST /api/store/projects/:project_id/project_items
 *
 * Required params:
 * - item_type: 'folder' or 'product'
 * - For folder: name (required)
 * - For product: product_id (required)
 *
 * Optional params:
 * - parent_id: for nested items (folder hierarchy)
 * - folder_path: catalog folder segments; for products, backend creates missing folders
 * - position: integer (defaults to next available)
 * - notes: text
 *
 * @param {number|string} projectId
 * @param {{
 *   item_type: "folder" | "product",
 *   name?: string,
 *   product_id?: number|string,
 *   parent_id?: number|null,
 *   folder_path?: string[],
 *   position?: number,
 *   notes?: string
 * }} params
 * @returns {Promise<any>} Newly created item.
 */
export const createStoreProjectItem = async (projectId, params) => {
  const body = {
    item_type: params.item_type,
    ...(params.item_type === "folder" && params.name && { name: params.name }),
    ...(params.item_type === "product" &&
      params.product_id && { product_id: params.product_id }),
    ...(params.parent_id !== undefined && { parent_id: params.parent_id }),
    ...(Array.isArray(params.folder_path) &&
      params.folder_path.length > 0 && { folder_path: params.folder_path }),
    ...(params.position !== undefined && { position: params.position }),
    ...(params.notes && { notes: params.notes }),
  };
  const response = await axios.post(
    `/store/projects/${projectId}/project_items`,
    body
  );
  return response;
};

/**
 * Update an item.
 *
 * Backend endpoint: PATCH/PUT /api/store/projects/:project_id/project_items/:id
 *
 * Optional params: name, product_id, parent_id, position, notes
 *
 * @param {number|string} projectId
 * @param {number|string} itemId
 * @param {{
 *   name?: string,
 *   product_id?: number|string,
 *   parent_id?: number|null,
 *   position?: number,
 *   notes?: string
 * }} params
 * @returns {Promise<any>} Updated item.
 */
export const updateStoreProjectItem = async (projectId, itemId, params) => {
  const body = {};
  if (params.name !== undefined) body.name = params.name;
  if (params.product_id !== undefined) body.product_id = params.product_id;
  if (params.parent_id !== undefined) body.parent_id = params.parent_id;
  if (params.position !== undefined) body.position = params.position;
  if (params.notes !== undefined) body.notes = params.notes;

  const response = await axios.patch(
    `/store/projects/${projectId}/project_items/${itemId}`,
    body
  );
  return response;
};

/**
 * Delete an item.
 *
 * Backend endpoint: DELETE /api/store/projects/:project_id/project_items/:id
 * Cascades to delete children (via model dependent: :destroy)
 *
 * @param {number|string} projectId
 * @param {number|string} itemId
 * @returns {Promise<any>} Response from API.
 */
export const deleteStoreProjectItem = async (projectId, itemId) => {
  const response = await axios.delete(
    `/store/projects/${projectId}/project_items/${itemId}`
  );
  return response;
};

export const destroyStoreProject = async (projectId) => {
  const response = await axios.delete(`/store/projects/${projectId}`);
  return response;
};

export const cloneStoreProjectItem = async (
  projectId,
  itemId,
  { parentId } = {}
) => {
  const body = {};
  if (parentId !== undefined) {
    body.parent_id = parentId;
  }
  return axios.post(
    `/store/projects/${projectId}/project_items/${itemId}/clone`,
    body
  );
};

/**
 * Fetch all images for a project.
 *
 * Backend endpoint: GET /store/project_images?project_id=:projectId
 *
 * @param {number|string} projectId
 * @returns {Promise<any[]>} Array of image records.
 */
export const getStoreProjectImages = async (projectId) => {
  const response = await axios.get(`${STORE_API_BASE}/project_images`, {
    params: { project_id: projectId },
  });
  return response;
};

/**
 * Upload an image for a project.
 *
 * Backend endpoint: POST /store/project_images
 * Payload: multipart/form-data with image, title, project_id.
 *
 * @param {{ projectId: number|string, file: File, title?: string }} params
 * @returns {Promise<any>} Newly created image record.
 */
export const createStoreProjectImage = async ({ projectId, file, title }) => {
  const formData = new FormData();
  formData.append("project_id", projectId);
  if (title) formData.append("title", title);
  formData.append("image", file);

  const response = await axios.post(
    `${STORE_API_BASE}/project_images`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response;
};

/**
 * Fetch all tags (pins) for a given project image.
 *
 * Backend endpoint: GET /store/project_image_tags?image_id=:imageId
 *
 * @param {number|string} imageId
 * @returns {Promise<any[]>} Array of tag records with x, y, and project_item_id.
 */
export const getStoreProjectImageTags = async (imageId) => {
  const response = await axios.get(`${STORE_API_BASE}/project_image_tags`, {
    params: { image_id: imageId },
  });
  return response;
};

/**
 * Create a new image tag (Pinterest-style pin) for a project image.
 *
 * Backend endpoint: POST /store/project_image_tags
 *
 * @param {{
 *   projectImageId: number|string,
 *   projectId: number|string,
 *   projectItemId: number|string,
 *   x: number,
 *   y: number
 * }} params
 * @returns {Promise<any>} Newly created tag record.
 */
export const createStoreProjectImageTag = async ({
  projectImageId,
  projectId,
  projectItemId,
  x,
  y,
}) => {
  const body = {
    project_image_id: projectImageId,
    project_id: projectId,
    project_item_id: projectItemId,
    x,
    y,
  };
  const response = await axios.post(
    `${STORE_API_BASE}/project_image_tags`,
    body
  );
  return response;
};

export const getPriceList = async (params = {}) => {
  const response = await axios.get(`${STORE_API_BASE}/price_list`, {
    params: Object.keys(params).length ? params : undefined,
  });
  const body = response?.data ?? response;
  if (Array.isArray(body?.pages)) return body.pages;
  if (Array.isArray(body)) return body;
  return [];
};

export const getSignedToken = async ({ userId, resource }) => {
  return await axios.post(`store/users/${userId}/signed_token`, {
    resource,
  });
};

export const createStoreInvite = async ({ mobile, name, store_dealer_id }) => {
  return await axios.post(`${STORE_API_BASE}/invites`, {
    mobile,
    name,
    store_dealer_id,
  });
};

export const getDealerInvites = async (dealerId) => {
  return await axios.get(`${STORE_API_BASE}/dealers/${dealerId}/invites`);
};

export { getCatalogProduct, buildConfigParams, CONFIG_KEYS };
export default {
  getProducts,
  addStoreProductToCartProject,
  getCatalogProduct,
  createProduct,
  buildConfigParams,
  CONFIG_KEYS,
  getBrochureForPrint,
  getCatalogBrochureForPrint,
  getPriceList,
  registerStoreUser,
  verifyStoreOtp,
  getStoreProjects,
  createStoreProject,
  getStoreProjectItems,
  getStoreProjectFolders,
  getStoreProjectInstallation,
  getStoreProjectHardwareList,
  getStoreProjectSummary,
  updateStoreProject,
  duplicateStoreProject,
  updateStoreProjectStatus,
  destroyStoreProject,
  getStoreProjectItem,
  createStoreProjectItem,
  updateStoreProjectItem,
  deleteStoreProjectItem,
  cloneStoreProjectItem,
  getStoreProjectImages,
  createStoreProjectImage,
  getStoreProjectImageTags,
  createStoreProjectImageTag,
  getSignedToken,
  createStoreInvite,
  getDealerInvites,
};
